import {createContext, useEffect, useState} from "react";
import bosService from "../../../../../services/web/bos-service.js";
import {useQuery} from "@tanstack/react-query";
import {useParams} from "react-router";

export const BoShowContext = createContext(undefined);

const API_KEEPER = import.meta.env.VITE_API_KEEPER;
const API_DESPACHO = import.meta.env.VITE_API_DESPACHO;

const BosShowProvider = ({children}) => {
    const {id: idRaw} = useParams();
    const id = idRaw?.replace("-", "/");

    const [isLoading, setIsLoading] = useState(false);

    const [boData, setBoData] = useState({});
    const [urlAttach, setUrlAttach] = useState(API_KEEPER + "/ged/attach/");
    const [gedAttachIds, setGedAttachIds] = useState(undefined);

    const [isSync, setIsSync] = useState(false);
    const [lastSync, setLastSync] = useState(false);
    const [syncId, setSyncId] = useState(undefined);
    const [printOptions, setPrintOptions] = useState({
        requester: false,
        individual: false,
        approvalSignature: false,
        approvalName: false,
        image: false,
        attachFullSize: false
    });
    const [individualShowData, setIndividualShowData] = useState([]);
    const [attachesToHide, setAttachesToHide] = useState([]);
    const [modalAttach, setModalAttach] = useState({open: false, attach: undefined});

    const [attaches, setAttaches] = useState([]);

    const {data, isLoading: isLoadingFetch, refetch} = useQuery({
        queryFn: () => bosService.getBo(id),
        queryKey: ["bo-data"],
        enabled: !!id
    });

    useEffect(() => {
        if (!data) return;

        if (data.boContent.bogcm.bogcm.data) {
            setAttributesByData()
        } else if (data.boContent.syncs.length > 0) {
            setAttributesBySync(0);
        }
    }, [data]);

    useEffect(() => {
        if (Object.keys(boData).length === 0) return;

        const newAttaches = [];

        boData.individualList?.forEach(i => {
            if (i.attachs) newAttaches.push(...i.attachs);
        });

        boData.apprehensionList?.filter(item => !item?.relatedVehicle).forEach(a => {
            const attachment = a?.attachs?.[0];
            if (attachment?.attachId) newAttaches.push(attachment);
        });

        boData.stolenItemList?.forEach(a => {
            const attachment = a?.attachs?.[0];
            if (attachment?.attachId) newAttaches.push(attachment);
        });

        boData.vehicleList?.forEach(v => {
            if (v.attachs) newAttaches.push(...v.attachs);
        });

        if (boData.croqui?.attachs) newAttaches.push(...boData.croqui.attachs);

        boData.otherDocumentsList?.forEach(d => {
            if (d.attachs) newAttaches.push(...d.attachs);
        });

        setAttaches(newAttaches);
    }, [boData]);

    const setAttributesByData = () =>{
        setBoData(data.boContent.bogcm.bogcm.data);
        setIsSync(false);
        setUrlAttach(API_KEEPER + "/ged/attach/");
        setSyncId(undefined);
    }

    const setAttributesBySync = (positionSync) =>{
        setLastSync(positionSync === 0);

        const boSyncView = data.boContent.syncs[positionSync];

        setSyncId(boSyncView.id);
        setBoData(boSyncView.data.data);
        setGedAttachIds(boSyncView.data.gedAttachIds);
        setIsSync(true);
        setUrlAttach(API_DESPACHO + "/bogcm/sync/attach/" + boSyncView.id);
    }

    const verifySyncUrl = (attachId) => {
        const attach = attaches.find(a => a.attachId === attachId);
        const isPdf = attach?.extension === "application/pdf" || attach?.extension === "pdf";
        const useGed = !isSync || gedAttachIds?.includes(attachId);

        if (useGed) {
            return isPdf
                ? `${API_KEEPER}/ged/attach/print/pdf`
                : `${API_KEEPER}/ged/attach`;
        }

        return isPdf
            ? `${API_DESPACHO}/bogcm/sync/attach/${syncId}/print/pdf`
            : `${API_DESPACHO}/bogcm/sync/attach/${syncId}`;
    }

    const handleAttachToHide = (id) => {
        setAttachesToHide(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
    }

    const value = {
        id, idRaw,
        refetch, dataFetch: data?.boContent, tagsId: data?.tagsId ?? [], boData,
        isSync, lastSync, syncId,
        urlAttach, gedAttachIds,
        verifySyncUrl,
        isLoadingFetch, isLoading, setIsLoading,
        setAttributesByData, setAttributesBySync,
        printOptions, setPrintOptions,
        individualShowData, setIndividualShowData,
        attachesToHide, handleAttachToHide,
        modalAttach, setModalAttach,
        offsetUTC: data?.offsetUTC,
        dispatchId: data?.dispatchId,
        otherDocumentTypes: data?.otherDocumentTypes ?? []
    }

    return (
        <BoShowContext value={value}>
            {children}
        </BoShowContext>
    )
}

export default BosShowProvider;